import { createAsyncThunk, isFulfilled } from "@reduxjs/toolkit";
import {
  createMsgCommandAsyncThunk,
  createQueryAsyncThunk,
} from "../../app/createApiAsyncThunk";
import { AppDispatch, RootState } from "../../app/store";
import { MessageResponse } from "../../common/types/apiTypes";
import { Options } from "../../common/types/requestOptions";
import { workLocationsByIds } from "../customer/customerAPI";
import { CustomerUserAccount } from "../customer/types/queries";
import { listTalents } from "../talents/talentAPI";
import { User } from "../user/user";
import { getCoreUsers } from "../user/userAPI";
import {
  assignConversation,
  createConversation,
  createConversationMessage,
  getConversationAttachmentDownloadUrl,
  getInboxSummary,
  listConversationMessages,
  listConversations,
  listTeams,
  listTopics,
  markConversationMessagesRead,
  transferConversation as transferConversationRequest,
  updateConversationStatus,
} from "./messagingApi";
import {
  AssignConversationThunk,
  AssignConversationThunkResponse,
  ConversationAttachmentDownloadUrl,
  ConversationAttachmentDownloadUrlParams,
  CreateConversation,
  CreateConversationMessage,
  CreateConversationMessageThunk,
  CreateConversationMessageThunkResponse,
  CreateConversationResponse,
  CreateConversationThunk,
  MarkConversationMessagesRead,
  MarkConversationMessagesReadResponse,
  TransferConversation,
  UpdateConversationStatus,
} from "./types/commands";
import { ParticipantType } from "./types/common";
import {
  ChatMessage,
  Conversation,
  InboxSummary,
  ListConversationMessagesFiltersThunk,
  ListConversationMessagesResponse,
  ListConversations,
  ListConversationsFiltersThunk,
  ListTeams,
  ListTopics,
  MessageType,
  MessagingTeam,
  Participant,
  SearchConversationsFiltersThunk,
} from "./types/queries";

export type TransferConversationResult = {
  userId: string;
  initiatorId?: string;
};

interface AddConversationDetails {
  id: string;
  participants: Participant[];
}

export const fetchTopics = createQueryAsyncThunk<
  MessageResponse<ListTopics>,
  void
>("messaging/fetchTopics", listTopics);

export const fetchInboxSummary = createQueryAsyncThunk<
  MessageResponse<InboxSummary>,
  void
>("messaging/fetchInboxSummary", getInboxSummary);

export const fetchConversations = createAsyncThunk<
  MessageResponse<ListConversations>,
  ListConversationsFiltersThunk,
  { state: RootState }
>(
  "messaging/fetchConversations",
  async (filters: ListConversationsFiltersThunk, { requestId }) => {
    const { reset, isInitial, ...rest } = filters;
    const listConv = await listConversations(rest, {
      requestId,
    });
    const conversations = await enrichConversations(
      requestId,
      listConv.result.conversations,
    );

    return {
      ...listConv,
      result: {
        ...listConv.result,
        conversations,
      },
    };
  },
);

const searchConversationsRequest = async (
  filters: SearchConversationsFiltersThunk,
  options: Options,
): Promise<MessageResponse<ListConversations>> => {
  const { search, limit } = filters;
  const seenTokens = new Set<string>();
  if (filters.nextToken) seenTokens.add(filters.nextToken);

  const fetchPage = (pageToken?: string) =>
    listConversations(
      {
        search,
        ...(pageToken ? { nextToken: pageToken } : {}),
        ...(limit ? { limit } : {}),
      },
      options,
    );

  // A filtered DynamoDB page can be empty while nextToken still exists.
  // Virtuoso handles a short page that still has rows (endReached -> load more).
  let listConv = await fetchPage(filters.nextToken);
  while (
    listConv.result.conversations.length === 0 &&
    listConv.result.nextToken &&
    !seenTokens.has(listConv.result.nextToken)
  ) {
    seenTokens.add(listConv.result.nextToken);
    listConv = await fetchPage(listConv.result.nextToken);
  }

  const conversations = await enrichConversations(
    options.requestId ?? "",
    listConv.result.conversations,
  );

  return {
    ...listConv,
    result: {
      ...listConv.result,
      conversations,
    },
  };
};

export const searchConversations = createQueryAsyncThunk<
  MessageResponse<ListConversations>,
  SearchConversationsFiltersThunk
>("messaging/searchConversations", searchConversationsRequest);

export const fetchConversationMessages = createAsyncThunk<
  MessageResponse<ListConversationMessagesResponse>,
  ListConversationMessagesFiltersThunk,
  { state: RootState }
>(
  "messaging/fetchConversationMessages",
  async (filters: ListConversationMessagesFiltersThunk, { requestId }) => {
    const { isInitial, ...rest } = filters;
    const listConvMsg = await listConversationMessages(rest, {
      requestId,
    });

    // enrich response model with Talent status and Customer details
    const enrichedParticipants = await addConversationParticipantDetails(
      requestId,
      [
        {
          id: listConvMsg.result.conversationId,
          participants: listConvMsg.result.participants,
        },
      ],
    );

    return {
      ...listConvMsg,
      result: {
        ...listConvMsg.result,
        participants: enrichedParticipants[0].participants,
      },
    };
  },
);

export const updateConvStatus = createMsgCommandAsyncThunk<
  UpdateConversationStatus,
  MessageResponse<any>
>("messaging/updateConversationStatus", updateConversationStatus);

export const createNewConversation = createAsyncThunk<
  { response: MessageResponse<CreateConversationResponse>; initiator: User },
  CreateConversationThunk,
  { state: RootState }
>("messaging/createConversation", async (body, { getState, requestId }) => {
  const user = getState().user.user!;
  const command: CreateConversation = {
    type: body.type,
    participantIds: body.participantIds,
    ...(body.topic ? { topic: body.topic } : {}),
  };
  const response = await createConversation(command, { requestId });
  return { response, initiator: user };
});

export const createNewConversationMessage = createAsyncThunk<
  CreateConversationMessageThunkResponse,
  CreateConversationMessageThunk,
  { state: RootState }
>(
  "messaging/createConversationMessage",
  async (body, { getState, requestId }) => {
    const user = getState().user.user!;

    const { text, conversationId, id, attachments } = body;
    const command: CreateConversationMessage = { id, conversationId, text };
    const response = await createConversationMessage(command, { requestId });

    // TODO: error handling

    const message: ChatMessage = {
      id: response.result.messageId,
      conversationId,
      senderId: user.id,
      text,
      messageType: MessageType.TEXT,
      sentAt: body.sentAt ?? new Date().toISOString(),
      ...(attachments?.length ? { attachments } : {}),
    };

    return { conversationId, message };
  },
);

export const assignConversationToMe = createAsyncThunk<
  AssignConversationThunkResponse,
  AssignConversationThunk,
  { state: RootState }
>("messaging/assignConversationToMe", async (body, { getState }) => {
  const user = getState().user.user!;
  const payload = {
    ...body,
    participantId: user.id,
  };
  await assignConversation(payload);

  // TODO: error handling
  return { participantId: user.id };
});

export const fetchTeams = createAsyncThunk<MessagingTeam[], void>(
  "messaging/fetchTeams",
  async (_arg, { requestId }) => {
    const teams: MessagingTeam[] = [];
    let nextToken: string | undefined;
    do {
      const page: MessageResponse<ListTeams> = await listTeams(
        {
          limit: 500,
          ...(nextToken ? { nextToken } : {}),
        },
        { requestId },
      );
      teams.push(...page.result.items);
      nextToken = page.result.nextToken;
    } while (nextToken);
    return teams;
  },
);

export const transferConversation = createAsyncThunk<
  TransferConversationResult,
  TransferConversation,
  { state: RootState }
>("messaging/transferConversation", async (body, { getState, requestId }) => {
  await transferConversationRequest(body, { requestId });
  const userId = getState().user.user?.id ?? "";
  const initiatorId =
    getState().messaging.conversations.entities[body.conversationId]?.initiator
      .id;
  return { userId, initiatorId };
});

export const downloadConversationAttachment = createQueryAsyncThunk<
  ConversationAttachmentDownloadUrl,
  ConversationAttachmentDownloadUrlParams
>(
  "messaging/downloadConversationAttachment",
  getConversationAttachmentDownloadUrl,
);

const openAttachmentUrl = (url: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const openConversationAttachment = (
  dispatch: AppDispatch,
  params: ConversationAttachmentDownloadUrlParams,
) =>
  dispatch(downloadConversationAttachment(params)).then((action) => {
    if (isFulfilled(action)) openAttachmentUrl(action.payload.url);
  });

export const markMessagesRead = createMsgCommandAsyncThunk<
  MarkConversationMessagesRead,
  MessageResponse<MarkConversationMessagesReadResponse>
>("messaging/markMessagesRead", markConversationMessagesRead);

const enrichConversations = async (
  requestId: string,
  conversations: Conversation[],
): Promise<Conversation[]> => {
  const enrichedParticipants = await addConversationParticipantDetails(
    requestId,
    conversations,
  );
  return conversations.map((conversation) => {
    const participants = enrichedParticipants.find(
      (participant) => participant.id === conversation.id,
    )!.participants;
    return { ...conversation, participants };
  });
};

const addConversationParticipantDetails = async (
  requestId: string,
  conversations: AddConversationDetails[],
): Promise<AddConversationDetails[]> => {
  const { talentIds, customerIds } = conversations.reduce(
    (result, conversation) => {
      conversation.participants.forEach((p) => {
        if (p.type === ParticipantType.TALENT) {
          result.talentIds.push(p.id);
        }
        if (p.type === ParticipantType.CUSTOMER) {
          result.customerIds.push(p.id);
        }
      });

      return result;
    },
    { talentIds: [] as string[], customerIds: [] as string[] },
  );

  const [listTal, listCus] = await Promise.all([
    talentIds.length ? listTalents({ requestId }, talentIds) : null,
    customerIds.length
      ? getCoreUsers({ type: "Customer", userIds: customerIds }, { requestId })
      : null,
  ]);

  const workLocationIds: string[] = [];
  listCus?.data.forEach((customer) => {
    const account = customer.account as CustomerUserAccount;
    if (account.customerUserWorkLocationIds?.length) {
      workLocationIds.push(...account.customerUserWorkLocationIds);
    }
  });

  const workLocations = workLocationIds.length
    ? await workLocationsByIds(
        { workLocationIds: [...new Set(workLocationIds)] },
        { requestId },
      )
    : null;

  const updatedConversations = conversations.map((conversation) => {
    const participants = conversation.participants.map((participant) => {
      const talentStatus =
        participant.type === ParticipantType.TALENT
          ? listTal?.data.find((talent) => talent.id === participant.id)
              ?.account?.status
          : undefined;
      const customerUser =
        participant.type === ParticipantType.CUSTOMER
          ? listCus?.data.find((customer) => customer.id === participant.id)
          : undefined;
      const customerWorkLocationNames = customerUser
        ? workLocations
            ?.filter((workLocation) => {
              const ids = (customerUser.account as CustomerUserAccount)
                .customerUserWorkLocationIds;
              return ids?.includes(workLocation.workLocationId);
            })
            .map((workLocation) => workLocation.name)
        : undefined;

      return { ...participant, talentStatus, customerWorkLocationNames };
    });

    return { ...conversation, participants };
  });

  return updatedConversations;
};
